import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IFormFieldInputBaseProps } from '@components/form-fields';
import { IEnhancedMenuItem } from '../table/types';
import EnhancedButton from '../button/EnhancedButton';
import EnhancedMenu from './EnhancedMenu';
import { MAIN_THEME } from '@styles';
import { lighten } from '@mui/material';

type ActionBarItems = Omit<IEnhancedMenuItem, 'onClick'> & {
  onClick: () => void;
};

export interface IActionBarProps extends IFormFieldInputBaseProps {
  items?: ActionBarItems[];
  title?: string;
  disabled?: boolean;
  inline?: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    position: 'relative',
  },
  menuButton: {
    position: 'relative',
    borderRadius: 3,
    minHeight: 32,
    minWidth: 80,
    border: `1px ${MAIN_THEME.color.white86} solid`,
    boxSizing: 'border-box',
    padding: '0 15px',
    backgroundColor: MAIN_THEME.color.white,
    fontFamily: MAIN_THEME.font.regular,
    textTransform: 'capitalize',
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    textAlign: 'center',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.4,
      pointerEvents: 'all',
      color: MAIN_THEME.color.black,
    },
  },
}));

const SimpleActionBar: React.FC<IActionBarProps> = ({
  title = 'Actions',
  items = [],
  inline = false,
}) => {
  const actionClasses = useStyles().classes;

  const primaryActions = items.filter((i) => i.isPrimary && !i.hidden);

  const secondaryActions = items.filter((i) => !i.isPrimary && !i.hidden);

  return (
    <section title={title} className={actionClasses.container}>
      {primaryActions.map((item) => (
        <EnhancedButton
          key={item.name}
          disabled={item.disabled}
          onClick={item.onClick}
          isPrimary
        >
          {item.title}
        </EnhancedButton>
      ))}
      {inline ? (
        secondaryActions.map((item) => (
          <EnhancedButton
            key={item.name || item.title}
            disabled={item.disabled}
            onClick={item.onClick}
          >
            {item.title}
          </EnhancedButton>
        ))
      ) : secondaryActions.length > 1 ? (
        <EnhancedMenu
          title="Actions"
          items={secondaryActions}
          itemClassName={actionClasses.menuButton}
        />
      ) : (
        secondaryActions.length > 0 && (
          <EnhancedButton
            key={secondaryActions?.[0].name}
            disabled={secondaryActions?.[0].disabled}
            onClick={secondaryActions?.[0].onClick}
          >
            {secondaryActions?.[0].title}
          </EnhancedButton>
        )
      )}
    </section>
  );
};

export default SimpleActionBar;
